import React, { useState, useEffect, useCallback } from 'react';
import { getVeryNearestTaxiStands, TaxiStand, getLocationContext, testBasicAPI, analyzeProximity, test500mRadius } from '../api/taxiStands';
import { useAnimation, useStaggerAnimation } from '../hooks/useAnimations';
import './TaxiStands.css';

interface TaxiStandsProps {
    userLat?: number;
    userLon?: number;
    isVisible: boolean;
    onClose: () => void;
}

const TaxiStands: React.FC<TaxiStandsProps> = ({ userLat, userLon, isVisible, onClose }) => {
    const [taxiStands, setTaxiStands] = useState<TaxiStand[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastLocation, setLastLocation] = useState<{ lat: number; lon: number } | null>(null);
    const [locationContext, setLocationContext] = useState<{
        area: string;
        density: 'high' | 'medium' | 'low';
        recommendedRadius: number;
        description: string;
    } | null>(null);

    // Animation hooks
    const modalAnimation = useAnimation({
        type: 'slideUp',
        trigger: 'mount',
        duration: 400
    });

    const { addElement: addStandElement, animateAll: animateStands } = useStaggerAnimation(
        taxiStands.length,
        {
            type: 'fadeInUp',
            trigger: 'manual',
            staggerDelay: 100
        }
    );

    const fetchNearbyTaxiStands = useCallback(async (lat: number, lon: number) => {
        setIsLoading(true);
        setError(null);

        try {
            // Get location context for spatial calculation
            const context = getLocationContext(lat, lon);
            setLocationContext(context);
            
            console.log(`📍 Location: ${context.area}`);
            console.log(`🎯 Search radius: ${context.recommendedRadius}km (${context.density} density)`);
            console.log(`📝 ${context.description}`);

            // Use very nearest stands API for closest proximity (within 0.5km)
            console.log('🔍 Starting 500m radius search for walkable stands...');
            const response = await getVeryNearestTaxiStands(lat, lon, 500); // 0.5km max distance
            
            if (response.success && response.data.length > 0) {
                setTaxiStands(response.data);
                console.log(`✅ Found ${response.data.length} nearby taxi stands`);
                
                // Log proximity analysis for each stand
                response.data.forEach((stand, index) => {
                    const proximity = analyzeProximity(stand, lat, lon);
                    console.log(`🏃 Stand ${index + 1}: ${stand.locationName.en} - ${proximity.recommendation} (${Math.round(stand.distance || 0)}m)`);
                });

                // Trigger stagger animation for taxi stands
                setTimeout(() => {
                    animateStands();
                }, 200);
            } else if (response.success && response.data.length === 0) {
                console.log('⚠️ No stands within 500m found, trying test function...');
                
                // Try the test function to debug the issue
                const testResponse = await test500mRadius(lat, lon);
                if (testResponse.success && testResponse.data.length > 0) {
                    setTaxiStands(testResponse.data);
                    console.log(`✅ Test function found ${testResponse.data.length} taxi stands`);
                } else {
                    console.log('⚠️ Test function also failed, trying basic API...');
                    
                    // Try basic API as final fallback
                    const basicResponse = await testBasicAPI();
                    if (basicResponse.success && basicResponse.data.length > 0) {
                        const standsWithDistance = basicResponse.data.map(stand => ({
                            ...stand,
                            distance: calculateDistance(lat, lon, stand.location.latitude, stand.location.longitude)
                        }))
                        .sort((a, b) => (a.distance || 0) - (b.distance || 0)).slice(0, 8);
                        
                        setTaxiStands(standsWithDistance);
                        console.log(`✅ Fallback: Found ${standsWithDistance.length} taxi stands using basic API`);
                    } else {
                        setError('No taxi stands found near your location. Please try a different location or check if you\'re in a taxi-friendly area.');
                    }
                }
            } else {
                setError(response.error || 'Failed to fetch taxi stands');
            }
        } catch (err) {
            setError('Failed to fetch taxi stands. Please try again.');
            console.error('Error fetching taxi stands:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isVisible && userLat && userLon) {
            // Check if we have a new location or need to refresh
            const locationChanged = !lastLocation || 
                Math.abs(lastLocation.lat - userLat) > 0.001 || 
                Math.abs(lastLocation.lon - userLon) > 0.001;

            if (locationChanged) {
                fetchNearbyTaxiStands(userLat, userLon);
                setLastLocation({ lat: userLat, lon: userLon });
            }
        }
    }, [isVisible, userLat, userLon, lastLocation, fetchNearbyTaxiStands, animateStands]);

    // Calculate distance between two points using Haversine formula
    const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
        const R = 6371e3; // Earth's radius in meters
        const φ1 = lat1 * Math.PI / 180;
        const φ2 = lat2 * Math.PI / 180;
        const Δφ = (lat2 - lat1) * Math.PI / 180;
        const Δλ = (lon2 - lon1) * Math.PI / 180;

        const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c; // Distance in meters
    };

    const handleRefresh = () => {
        if (userLat && userLon) {
            fetchNearbyTaxiStands(userLat, userLon);
        }
    };

    const formatDistance = (distanceMeters: number): string => {
        if (distanceMeters < 1000) {
            return `${Math.round(distanceMeters)}m`;
        } else {
            return `${(distanceMeters / 1000).toFixed(1)}km`;
        }
    };

    const getStatusColor = (status: string): string => {
        const lowerStatus = status.toLowerCase();
        if (lowerStatus.includes('urban')) return 'urban';
        if (lowerStatus.includes('cross harbour') || lowerStatus.includes('cross-harbour')) return 'cross-harbour';
        return 'special';
    };

    if (!isVisible) return null;

    return (
        <div className="taxi-stands-overlay animate-fade-in" onClick={onClose}>
                    <div 
                        className="taxi-stands-modal animate-slide-up" 
                        ref={modalAnimation.elementRef as React.RefObject<HTMLDivElement>}
                        onClick={(e) => e.stopPropagation()}
                    >
                <div className="taxi-stands-modal-header">
                    <div className="taxi-stands-modal-title">
                        <span className="taxi-icon">🚕</span>
                        <h2>
                            Nearest Taxi Stands
                            <br />
                            <span className="taxi-stands-title-zh">最近的的士站</span>
                        </h2>
                    </div>
                    <div className="taxi-stands-modal-actions">
                        <button
                            className="refresh-button"
                            onClick={handleRefresh}
                            disabled={isLoading}
                            aria-label="Refresh taxi stands"
                            title="Refresh taxi stands"
                        >
                            <svg className="refresh-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                                <path d="M21 3v5h-5"/>
                                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                                <path d="M3 21v-5h5"/>
                            </svg>
                        </button>
                        <button
                            className="close-modal-button"
                            onClick={onClose}
                            aria-label="Close taxi stands"
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="taxi-stands-modal-content">
                    {locationContext && (
                        <div className="location-context-info">
                            <div className="location-context-header">
                                <span className="location-icon">📍</span>
                                <span className="location-area">{locationContext.area}</span>
                                <span className={`density-badge density-${locationContext.density}`}>
                                    {locationContext.density} density
                                </span>
                            </div>
                            <p className="location-description">{locationContext.description}</p>
                            <p className="search-radius-info">
                                Showing walkable taxi stands within 800m (10 min walk)
                            </p>
                        </div>
                    )}
                    
                    {isLoading ? (
                        <div className="taxi-stands-loading">
                            <div className="loading-spinner">
                                <div className="spinner"></div>
                            </div>
                            <p>Finding nearby taxi stands...</p>
                        </div>
                    ) : error ? (
                        <div className="taxi-stands-error">
                            <div className="error-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="15" y1="9" x2="9" y2="15"/>
                                    <line x1="9" y1="9" x2="15" y2="15"/>
                                </svg>
                            </div>
                            <h3>Unable to load taxi stands</h3>
                            <p>{error}</p>
                            <button className="retry-button" onClick={handleRefresh}>
                                Try Again
                            </button>
                        </div>
                    ) : taxiStands.length === 0 ? (
                        <div className="taxi-stands-empty">
                            <div className="empty-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                                    <circle cx="12" cy="9" r="2.5"/>
                                </svg>
                            </div>
                            <h3>No Taxi Stands Found</h3>
                            <p>No taxi stands found in your area. Try refreshing or check a different location.</p>
                        </div>
                    ) : (
                        <div className="taxi-stands-list">
                            {taxiStands.map((stand, index) => {
                                const proximity = userLat && userLon ? analyzeProximity(stand, userLat, userLon) : null;
                                
                                return (
                                    <div 
                                        key={stand.id} 
                                        className="taxi-stand-card animate-fade-in-up"
                                        ref={(el) => el && addStandElement(el)}
                                        style={{ animationDelay: `${index * 100}ms` }}
                                    >
                                        <div className="taxi-stand-rank">
                                            <span className="rank-number">{index + 1}</span>
                                            {proximity && (
                                                <div className={`proximity-indicator proximity-${proximity.proximityLevel}`}>
                                                    {proximity.proximityLevel === 'immediate' && '🚶‍♂️'}
                                                    {proximity.proximityLevel === 'nearby' && '🏃‍♂️'}
                                                    {proximity.proximityLevel === 'walkable' && '🚶‍♀️'}
                                                    {proximity.proximityLevel === 'distant' && '🚗'}
                                                </div>
                                            )}
                                        </div>
                                        <div className="taxi-stand-content">
                                            <div className="taxi-stand-header">
                                                <h4 className="taxi-stand-name">{stand.locationName.en}</h4>
                                                {stand.distance && (
                                                    <span className="taxi-stand-distance">
                                                        {formatDistance(stand.distance)}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="taxi-stand-location">{stand.locationName.tc}</p>
                                            
                                            {proximity && (
                                                <div className="proximity-info">
                                                    <div className="proximity-recommendation">
                                                        {proximity.recommendation}
                                                    </div>
                                                    <div className="walking-time">
                                                        <span>🚶‍♂️ {proximity.walkingTime} min walk</span>
                                                        <a
                                                            href={`https://www.google.com/maps/dir/?api=1&destination=${stand.location.latitude},${stand.location.longitude}&travelmode=walking`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="taxi-stand-navigate"
                                                            aria-label={`Get walking directions to ${stand.locationName.en}`}
                                                            title={`Get walking directions to ${stand.locationName.en}`}
                                                        >
                                                            <svg className="navigate-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 6.5V7.5L21 9ZM3 9L9 7.5V6.5L3 7V9ZM12 8C8.5 8 5.5 10.5 5.5 14V16H6.5V14C6.5 11.2 8.7 9 12 9C15.3 9 17.5 11.2 17.5 14V16H18.5V14C18.5 10.5 15.5 8 12 8Z"/>
                                                                <path d="M8 20L10 18L12 20L14 18L16 20"/>
                                                            </svg>
                                                            <span className="navigate-text">Walk</span>
                                                        </a>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            <div className="taxi-stand-details">
                                                <span className="taxi-stand-district">{stand.district.en}</span>
                                                <span className={`taxi-stand-status ${getStatusColor(stand.status.en)}`}>
                                                    {stand.status.en}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {taxiStands.length > 0 && (
                    <div className="taxi-stands-footer">
                        <p className="taxi-stands-info">
                            Showing {taxiStands.length} nearest taxi stands
                            {userLat && userLon && (
                                <span className="location-info">
                                    from your current location
                                </span>
                            )}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaxiStands;
